import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "antd";
import { CommentOutlined, SendOutlined } from "@ant-design/icons";
import styles from "./style.module.scss";

const PREVIEW_MESSAGES = {
  tr: {
    user: "Bu ürün hakkında bilgi alabilir miyim?",
    bot: "Tabii. Seçenekleri birlikte netleştirebiliriz ya da satış ekibine bağlayabilirim.",
    cta: "Bilgi al",
    placeholder: "Mesajınızı yazın…",
    online: "Çevrimiçi",
    handoffLabel: "Satış ekibinden biri müsait. Sizi bir temsilciye bağlıyoruz.",
    handoffCta: "Satış ekibiyle konuş →",
    handoffChip: "Yüksek niyet + bilgi + lead",
  },
  en: {
    user: "Can you send pricing for 250 seats?",
    bot: "Absolutely. I can walk you through plans for 250 seats — or connect you with Sales for a tailored quote.",
    cta: "Get a quote",
    placeholder: "Type your message…",
    online: "Online",
    handoffLabel: "Someone from Sales is available. We'll connect you to a sales rep now.",
    handoffCta: "Talk to Sales →",
    handoffChip: "High intent + pricing + lead captured",
  },
};

const FREE_FOOTER_HREF =
  "https://pivony.com/products/sonic-prospect?utm_source=app.pivony.com&utm_medium=sonic_prospect_chat&utm_campaign=free_footer&utm_content=console_preview";

const SonicMark = () => (
  <svg
    className={styles.previewFooterIcon}
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false"
  >
    <circle
      cx="24"
      cy="24"
      r="21"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      opacity="0.35"
    />
    <circle
      cx="24"
      cy="24"
      r="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      opacity="0.55"
    />
    <circle
      cx="24"
      cy="24"
      r="9"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
    />
    <path
      d="M17.5 17.5c3.5 2 3.5 11 0 13M24 15.5v17M30.5 17.5c-3.5 2-3.5 11 0 13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChatWidgetPreview = ({ bot, websiteLabel }) => {
  const { t } = useTranslation();
  const showWatermark = !bot?.appearance?.hideWatermark;
  const lang = bot?.language === "en" ? "en" : "tr";
  const copy = PREVIEW_MESSAGES[lang];
  const brandColor = bot?.appearance?.brandColor || "#7c3aed";
  const botTitle = (bot?.name || "").trim() || t("sonic_prospect.preview_bot_fallback");
  const siteName =
    websiteLabel ||
    (bot?.websiteUrl || "")
      .replace(/^https?:\/\//i, "")
      .split("/")[0] ||
    t("sonic_prospect.preview_site_fallback");

  return (
    <div className={styles.previewShell}>
      <div className={styles.previewSite}>
        <div className={styles.previewSiteHeader}>
          <span className={styles.previewSiteLogo}>{siteName}</span>
          <span className={styles.previewSiteNav}>Ana sayfa · Ürünler · İletişim</span>
        </div>
        <div className={styles.previewSiteHero}>
          <h3>{t("sonic_prospect.preview_hero_title")}</h3>
          <p>{t("sonic_prospect.preview_hero_desc")}</p>
          <Button
            type="primary"
            style={{ background: brandColor, borderColor: brandColor, color: "#fff" }}
          >
            {t("sonic_prospect.preview_hero_cta")}
          </Button>
        </div>
      </div>
      <div
        className={styles.previewWidget}
        style={{ "--sp-brand": brandColor }}
      >
        <div className={styles.previewWidgetHeader}>
          <CommentOutlined />
          <div>
            <strong>{botTitle}</strong>
            <span>{copy.online}</span>
          </div>
        </div>
        <div className={styles.previewWidgetBody}>
          <div className={styles.previewBubbleUser}>{copy.user}</div>
          <div className={styles.previewBubbleBot}>{copy.bot}</div>
          {bot?.handoff?.enabled ? (
            <div className={styles.previewHandoff}>
              <div className={styles.previewHandoffRow}>
                <span className={styles.previewHandoffCheck}>✓</span>
                <div>
                  <p>{copy.handoffLabel}</p>
                  <Button
                    type="primary"
                    className={styles.previewHandoffCta}
                    style={{ background: brandColor, borderColor: brandColor, color: "#fff" }}
                  >
                    {copy.handoffCta}
                  </Button>
                  <span className={styles.previewHandoffChip}>{copy.handoffChip}</span>
                </div>
              </div>
            </div>
          ) : bot?.goals?.lead_capture !== false ? (
            <Button
              size="small"
              type="primary"
              className={styles.previewCta}
              style={{ background: brandColor, borderColor: brandColor, color: "#fff" }}
            >
              {copy.cta}
            </Button>
          ) : null}
        </div>
        <div className={styles.previewWidgetInput}>
          <span>{copy.placeholder}</span>
          <SendOutlined />
        </div>
        {showWatermark ? (
          <a
            className={styles.previewFooter}
            href={FREE_FOOTER_HREF}
            target="_blank"
            rel="noopener noreferrer"
          >
            <SonicMark />
            <span>{t("sonic_prospect.free_footer_label")}</span>
          </a>
        ) : null}
      </div>
    </div>
  );
};

export default ChatWidgetPreview;
